import { api } from '@/lib/api';
import type {
  CreatePaymentFeeRuleV2Request,
  CreatePaymentMethodProfileRequest,
  PaymentFeeConfigView,
  PaymentFeeRuleV2View,
  PaymentMethodProfileView,
  ReviseReceiptPartFeeRequest,
  UpdatePaymentFeeRuleV2Request,
  UpdatePaymentMethodProfileRequest,
} from '@valiosascrm/shared';

// Story 65.20 — taxas por máquina e bandeira e taxa das partes recebidas. As rotas só
// respondem na clínica em que o recebimento em partes está ligado.

export interface ReceiptPartFee {
  rootPartId: string;
  /** Folha corrente da parte: a revisão de taxa precisa citá-la. */
  partId: string;
  version: number;
  receiptId: string;
  /** Forma de pagamento (65.41). Dinheiro não tem taxa: a tela não oferece informar nem ajustar. */
  paymentMethod: string | null;
  label: string;
  cardBrand: string | null;
  installments: number | null;
  /** O que passou pelo meio de pagamento, em centavos. */
  feeBaseCents: number;
  appliedAmount: number;
  feeState: 'resolved' | 'pending_review' | 'confirmed_zero';
  feeRuleSource: string | null;
  feeAmount: number | null;
  netAmount: number | null;
  effectivePaidAt: string;
  patientName: string | null;
}

export interface FeeRevisionResult {
  replay: boolean;
  revision: { id: string; kind: string; reason: string; createdAt: string };
  part: ReceiptPartFee;
  previous: { partId: string; feeState: string; feeAmount: number | null; netAmount: number | null };
}

export async function getPaymentFeeConfig(): Promise<PaymentFeeConfigView> {
  return (await api.get<PaymentFeeConfigView>('/payment-fees/v2/config')).data;
}

export async function createPaymentMethodProfile(request: CreatePaymentMethodProfileRequest): Promise<PaymentMethodProfileView> {
  return (await api.post<PaymentMethodProfileView>('/payment-fees/v2/profiles', request)).data;
}

export async function updatePaymentMethodProfile(id: string, request: UpdatePaymentMethodProfileRequest): Promise<PaymentMethodProfileView> {
  return (await api.patch<PaymentMethodProfileView>(`/payment-fees/v2/profiles/${id}`, request)).data;
}

export async function createPaymentFeeRuleV2(request: CreatePaymentFeeRuleV2Request): Promise<PaymentFeeRuleV2View> {
  return (await api.post<PaymentFeeRuleV2View>('/payment-fees/v2/rules', request)).data;
}

export async function updatePaymentFeeRuleV2(id: string, request: UpdatePaymentFeeRuleV2Request): Promise<PaymentFeeRuleV2View> {
  return (await api.patch<PaymentFeeRuleV2View>(`/payment-fees/v2/rules/${id}`, request)).data;
}

export async function removePaymentFeeRuleV2(id: string, expectedRevision: number): Promise<{ removed: true }> {
  return (await api.delete<{ removed: true }>(`/payment-fees/v2/rules/${id}`, { params: { expectedRevision } })).data;
}

/** Story 65.22 (C6): a lista vem em páginas deste tamanho; `after` é a última parte lida. */
export const RECEIPT_PART_FEES_PAGE = 100;

export async function getReceiptPartFees(state: 'pending' | 'settled', after?: string): Promise<ReceiptPartFee[]> {
  return (await api.get<ReceiptPartFee[]>('/cash-flow/receipt-parts/fees', { params: { state, ...(after ? { after } : {}) } })).data;
}

/** Story 65.22 (C6): quantas taxas estão a conferir, pela idade do pagamento. */
export interface ReceiptPartFeeAges {
  total: number;
  upTo7Days: number;
  from8To30Days: number;
  over30Days: number;
}

export async function getReceiptPartFeeAges(): Promise<ReceiptPartFeeAges> {
  return (await api.get<ReceiptPartFeeAges>('/cash-flow/receipt-parts/fees/summary')).data;
}

export async function reviseReceiptPartFee(rootPartId: string, request: ReviseReceiptPartFeeRequest): Promise<FeeRevisionResult> {
  return (await api.post<FeeRevisionResult>(`/cash-flow/receipt-parts/${rootPartId}/fee-revisions`, request)).data;
}
